import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { RootState, AppDispatch } from "@store/store";
import { ninoxAuthService } from "@store/services/ninoxAuthService";
import {
  PasswordRecoveryState,
  SendEmailPayload,
  PasswordChangeStep
} from "@models/password-recovery.model";
import { selectUserEmail } from '@store/selectors/user.selectors';

// Check if we're in a browser environment
const isBrowser = typeof window !== 'undefined';

// Load persisted state from localStorage
const loadState = (): Partial<PasswordRecoveryState> => {
  if (!isBrowser) return {};
  
  try {
    const serializedState = localStorage.getItem('passwordRecoveryState');
    if (serializedState === null) {
      return {};
    }
    return JSON.parse(serializedState);
  } catch (err) {
    console.error('Error loading persisted state:', err);
    return {};
  }
};

// Initial state with persisted values
const persistedState = loadState();

const initialState: PasswordRecoveryState = {
  email: persistedState.email || null,
  isEmailSent: persistedState.isEmailSent || false,
  isLoading: false,
  error: null,
  verificationCode: persistedState.verificationCode || "",
  passwordChangeStatus: persistedState.passwordChangeStatus || null,
  passwordChangeError: null,
  timestamp: persistedState.timestamp || null,
  currentStep: persistedState.currentStep || PasswordChangeStep.EMAIL_INPUT,
};

export const handleEmailSubmitThunk = createAsyncThunk
  <void,
  string,
  { dispatch: AppDispatch; state: RootState }
>(
  "passwordRecovery/handleEmailSubmit",
  async (email, { dispatch, rejectWithValue }) => {
    dispatch(startLoading());

    try {
      const result = await dispatch(
        ninoxAuthService.endpoints.resetPasswordCode.initiate({ mail: email })
      ).unwrap();
      if (!result.error) {
        const timestamp = Date.now();
        dispatch(sendEmailSuccess({ email, timestamp }));
      } else {
        dispatch(sendEmailFailure(result.message));
      }
    } catch (error) {
      console.error("Error during email submission:", error);
      dispatch(sendEmailFailure("unexpected-error"));
      return rejectWithValue(error);
    }
  }
);

export const handlePasswordSubmitThunk = createAsyncThunk
  <void,
  { password: string },
  { dispatch: AppDispatch; state: RootState }
>(
  "passwordRecovery/handlePasswordSubmit",
  async ({ password }, { dispatch, getState, rejectWithValue }) => {
    dispatch(startLoading());
    const { email, verificationCode } = getState().passwordRecovery;

    if (!email || !verificationCode) {
      dispatch(
        changePasswordFailure("Missing email or verification code")
      );
      return rejectWithValue("Missing required fields");
    }

    try {
      const result = await dispatch(
        ninoxAuthService.endpoints.passwordReset2.initiate({
          mail: email,
          resetCode: verificationCode,
          newPassword: password,
        })
      ).unwrap();

      if (!result.error) {
        dispatch(changePasswordSuccess());
      } else {
        dispatch(changePasswordFailure(result.message));
      }
    } catch (error) {
      console.error("Error during password change:", error);
      dispatch(changePasswordFailure("unexpected-error"));
      return rejectWithValue(error);
    }
  }
);

export const resetStateWithUserEmail = () => (dispatch: AppDispatch, getState: () => RootState) => {
  const userEmail = selectUserEmail(getState());
  dispatch(resetStateWithUserEmailAction(userEmail));
};

// Helper function to safely save to localStorage
const saveToLocalStorage = (key: string, data: Record<string, unknown>) => {
  if (isBrowser) {
    try {
      localStorage.setItem(key, JSON.stringify(data));
    } catch (err) {
      console.error('Error saving to localStorage:', err);
    }
  }
};

const passwordRecoverySlice = createSlice({
  name: "passwordRecovery",
  initialState,
  reducers: {
    startLoading: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    sendEmailSuccess: (state, action: PayloadAction<SendEmailPayload>) => {
      state.isLoading = false;
      state.email = action.payload.email;
      state.isEmailSent = true;
      state.timestamp = action.payload.timestamp;
      state.currentStep = PasswordChangeStep.OTP_INPUT;
      
      // Persist state to localStorage
      const stateToSave = {
        email: state.email,
        isEmailSent: state.isEmailSent,
        timestamp: state.timestamp,
        currentStep: state.currentStep
      };
      saveToLocalStorage('passwordRecoveryState', stateToSave);
      state.currentStep = PasswordChangeStep.OTP_INPUT;
    },
    sendEmailFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    updateVerificationCode: (state, action: PayloadAction<string>) => {
      state.verificationCode = action.payload;
      if (action.payload.length === 6) {
        state.currentStep = PasswordChangeStep.PASSWORD_INPUT;
      }
      
      // Persist state to localStorage
      if (isBrowser) {
        const currentState = localStorage.getItem('passwordRecoveryState');
        const parsedState = currentState ? JSON.parse(currentState) : {};
        saveToLocalStorage('passwordRecoveryState', {
          ...parsedState,
          verificationCode: state.verificationCode,
          currentStep: state.currentStep
        });
      }
    },
    changePasswordSuccess: (state) => {
      state.isLoading = false;
      state.passwordChangeStatus = "success";
      state.currentStep = PasswordChangeStep.SUCCESS;
      
      // Persist state to localStorage
      if (isBrowser) {
        const currentState = localStorage.getItem('passwordRecoveryState');
        const parsedState = currentState ? JSON.parse(currentState) : {};
        saveToLocalStorage('passwordRecoveryState', {
          ...parsedState,
          passwordChangeStatus: state.passwordChangeStatus,
          currentStep: state.currentStep
        });
      }
    },
    changePasswordFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.passwordChangeError = action.payload;
    },
    resetState: (state) => {
      state.isEmailSent = false;
      state.verificationCode = "";
      state.passwordChangeStatus = null;
      state.passwordChangeError = null;
      state.currentStep = PasswordChangeStep.EMAIL_INPUT;
      state.timestamp = null;
      
      // Clear localStorage
      if (isBrowser) {
        localStorage.removeItem('passwordRecoveryState');
        localStorage.removeItem('mail');
      }
    },
    resetStateWithUserEmailAction: (state, action: PayloadAction<string | null | undefined>) => {
      // Reset all state first
      state.isEmailSent = false;
      state.verificationCode = "";
      state.passwordChangeStatus = null;
      state.passwordChangeError = null;
      state.currentStep = PasswordChangeStep.EMAIL_INPUT;
      state.timestamp = null;
      
      // Set email from user profile, ensuring we never assign undefined
      state.email = action.payload !== undefined ? action.payload : null;
      
      // Clear localStorage
      if (isBrowser) {
        localStorage.removeItem('passwordRecoveryState');
      }
    },
    setEmail: (state, action: PayloadAction<string>) => {
      state.email = action.payload;
      
      // Optional: Persistieren des neuen Email-Werts im localStorage
      if (isBrowser) {
        const currentState = localStorage.getItem('passwordRecoveryState');
        const parsedState = currentState ? JSON.parse(currentState) : {};
        saveToLocalStorage('passwordRecoveryState', {
          ...parsedState,
          email: action.payload
        });
      }
    },
    setCurrentStep: (state, action: PayloadAction<PasswordChangeStep>) => {
      state.currentStep = action.payload;
      
      // Persist state to localStorage
      if (isBrowser) {
        const currentState = localStorage.getItem('passwordRecoveryState');
        const parsedState = currentState ? JSON.parse(currentState) : {};
        saveToLocalStorage('passwordRecoveryState', {
          ...parsedState,
          currentStep: state.currentStep
        });
      }
    }
  },
});

export const {
  startLoading,
  sendEmailSuccess,
  sendEmailFailure,
  updateVerificationCode,
  changePasswordSuccess,
  changePasswordFailure,
  resetState,
  resetStateWithUserEmailAction,
  setEmail, 
  setCurrentStep
} = passwordRecoverySlice.actions;

export default passwordRecoverySlice.reducer;